import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiShoppingCart, FiTrendingUp, FiHeadphones, FiBarChart2 } = FiIcons;

const BenefitsSection = () => {
  const benefits = [
    {
      icon: FiShoppingCart,
      title: "Recover 25-40% of Abandoned Carts",
      description: "Proactively engage customers who are about to leave and offer them the help they need to complete their purchase.",
      metric: "One client recovered $5,400 in their first month",
      color: "from-emerald-500 to-teal-500",
      bgColor: "from-emerald-50 to-teal-50"
    },
    {
      icon: FiTrendingUp,
      title: "Increase Conversion by 18%",
      description: "Answer questions instantly, provide personalized recommendations, and guide visitors to the perfect product.",
      metric: "Turn more browsers into buyers",
      color: "from-blue-500 to-purple-500",
      bgColor: "from-blue-50 to-purple-50"
    },
    {
      icon: FiHeadphones,
      title: "Automate 80% of Support",
      description: "Free up your time by letting the AI handle repetitive questions about shipping, returns, and order status.",
      metric: "Save 20+ hours per week",
      color: "from-purple-500 to-pink-500",
      bgColor: "from-purple-50 to-pink-50"
    },
    {
      icon: FiBarChart2,
      title: "Gain Customer Insights",
      description: "Get automated daily reports on what your customers are asking for, what problems they're facing, and what they want to buy next.",
      metric: "Data-driven decisions every day",
      color: "from-orange-500 to-red-500",
      bgColor: "from-orange-50 to-red-50"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6 leading-tight">
            Stop Just Selling Products.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">
              Start Building a Conversion Machine.
            </span>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 overflow-hidden"
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${benefit.bgColor} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
              
              {/* Icon */}
              <div className="relative">
                <div className={`w-16 h-16 bg-gradient-to-br ${benefit.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={benefit.icon} className="text-white text-2xl" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 text-lg">
                  {benefit.description}
                </p>
                
                {/* Metric */}
                <div className="bg-gray-50 group-hover:bg-white rounded-xl p-4 transition-colors">
                  <p className="text-sm font-bold text-gray-900">{benefit.metric}</p>
                </div>
              </div>

              {/* Hover Effect Border */}
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${benefit.color} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300 -z-10`}></div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;